/* eslint-disable no-shadow */
import {useNumberInput} from '@chakra-ui/number-input';
import {useCallback, useEffect, useState} from 'react';
import {StorageKeys} from '../shared/consts';
import {useBasketContext} from './basket-context';

export interface QuatitySelectorProps {
  quantityInput: {
    value: number;
  };
  quantity: number;
  setQuantity: (value: number) => void;
  totalPrice: string;
  totalPriceInt: number;
  amountInput: {
    value: string;
  };
  loading: boolean;
  showRoundingMsg: boolean;
  showMaxPiecesError: boolean;
  incrementQuantity: () => void;
  decrementQuantity: () => void;
}

export const useQuantitySelector: (
  piecesRemaining: number,
  intPrice: number,
  storageKey: StorageKeys | string,
  maxAssetsPerUser: number,
) => QuatitySelectorProps = (
  piecesRemaining,
  intPrice,
  storageKey,
  maxAssetsPerUser,
) => {
  const {quantity, setQuantity, setStorageKey} = useBasketContext();
  const [showRoundingMsg, setShowRoundingMsg] = useState(false);
  const [showMaxPiecesError, setShowMaxPiecesError] = useState(false);

  useEffect(() => {
    if (storageKey) {
      setStorageKey(storageKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const inputRef = (e: any) => e.target.select();

  const {getInputProps} = useNumberInput({
    step: 1,
    defaultValue: quantity,
  });

  const handleChange = useCallback(
    (input: number) => {
      setShowMaxPiecesError(false);
      if (Number.isNaN(input)) {
        setQuantity(0);
      } else if (
        input > maxAssetsPerUser &&
        maxAssetsPerUser <= piecesRemaining
      ) {
        setShowMaxPiecesError(true);
        setQuantity(maxAssetsPerUser);
      } else if (input < piecesRemaining) {
        setQuantity(input);
      } else {
        setQuantity(piecesRemaining);
      }
    },
    [setQuantity, piecesRemaining, maxAssetsPerUser],
  );

  const input = getInputProps({
    onClick: (event) => inputRef(event),
    onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
      handleChange(parseInt(event.target.value, 10)),
  });

  const quantityInput = {
    ...input,
    value: quantity,
  };

  const [amount, setAmount] = useState<number>(0);

  const incrementQuantity = () => {
    setShowMaxPiecesError(false);
    if (quantity < piecesRemaining && quantity < maxAssetsPerUser) {
      setQuantity(quantity + 1);
    } else if (piecesRemaining > maxAssetsPerUser) {
      setShowMaxPiecesError(true);
      setQuantity(maxAssetsPerUser);
    } else {
      setQuantity(piecesRemaining);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) setQuantity(quantity - 1);
    else setQuantity(0);
  };

  useEffect(() => {
    setAmount(quantity * intPrice || 0);
  }, [quantity, intPrice]);

  const {getInputProps: getPriceInputProps} = useNumberInput({
    defaultValue: amount,
    max: maxAssetsPerUser,
  });

  const [loading, setLoading] = useState(false);

  const handleAmountChange = useCallback(
    (inputData: number) => {
      if (Number.isNaN(inputData)) {
        setQuantity(0);
        setLoading(false);
      } else {
        setAmount(inputData);
        setLoading(true);
      }
    },
    [setQuantity],
  );

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setShowMaxPiecesError(false);
      if (amount && intPrice) {
        const roundedQuantity = amount / intPrice;
        if (roundedQuantity !== quantity) {
          setShowRoundingMsg(true);
        }
        if (
          roundedQuantity > maxAssetsPerUser &&
          maxAssetsPerUser <= piecesRemaining
        ) {
          setShowMaxPiecesError(true);
          setQuantity(maxAssetsPerUser);
          setAmount(maxAssetsPerUser * intPrice);
        } else if (roundedQuantity > piecesRemaining) {
          setQuantity(piecesRemaining);
          setAmount(piecesRemaining * intPrice);
        } else {
          setQuantity(Math.ceil(roundedQuantity));
          // Required as if quantity doesn't change but the amount does,
          // this should be rounded to the correct value
          setAmount(Math.ceil(roundedQuantity) * (intPrice || 0));
        }
        setLoading(false);
      }
    }, 2000);

    return () => clearTimeout(delayDebounceFn);
  }, [
    amount,
    piecesRemaining,
    intPrice,
    quantity,
    setQuantity,
    maxAssetsPerUser,
  ]);

  const amountInputProps = getPriceInputProps({
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      handleAmountChange(parseInt(event.target.value.replace(/,/g, ''), 10));
    },
    onBlur: () => {
      setQuantity(Math.ceil(amount / intPrice));
      setShowRoundingMsg(false);
    },
  });

  const totalPriceInt = Math.ceil(amount);

  const totalPrice = totalPriceInt.toLocaleString('en-US');

  const amountInput = {
    ...amountInputProps,
    value: totalPrice,
  };

  return {
    quantityInput,
    quantity,
    setQuantity,
    totalPrice,
    amountInput,
    loading,
    showRoundingMsg,
    showMaxPiecesError,
    incrementQuantity,
    decrementQuantity,
    totalPriceInt,
  };
};
